import React, { createContext, useState, useEffect, useContext } from 'react';
import {
  getSessionDeadline,
  isSessionExpired,
  clearSessionExpiration,
  redirectToQrReader,
} from './Utills/sessionExpiration';
import { resolveColor } from './Utills/colors';

export const GlobalContext = createContext();

const apiImageUrl = (image) => {
  if (!image) return '';
  return image.startsWith('http') ? image : `${process.env.REACT_APP_URL_API}${image}`;
};

const SessionExpirationModal = ({ seconds, logo, color }) => {
  if (!seconds || seconds > 5) return null;

  return (
    <div className="session-expiration-overlay" role="presentation">
      <div className="session-expiration-modal" role="dialog" aria-modal="true" aria-labelledby="session-expiration-title">
        {logo && <img src={apiImageUrl(logo)} alt="Logo do hotel" className="session-expiration-logo" />}
        <h2 id="session-expiration-title">Sua sessão está terminando</h2>
        <p>
          Será necessário ler o QR Code novamente em{' '}
          <strong>{seconds} segundo{seconds === 1 ? '' : 's'}</strong>.
        </p>
        <button
          type="button"
          className="session-expiration-button"
          style={{ backgroundColor: color }}
          onClick={() => redirectToQrReader()}
        >
          Ler QR Code agora
        </button>
      </div>
    </div>
  );
};

export const GlobalStorage = ({ children }) => {
  const [data, setData] = useState(null);
  const [menu, setMenu] = useState(null);
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionDeadline, setSessionDeadline] = useState(null);
  const [sessionSecondsRemaining, setSessionSecondsRemaining] = useState(null);
  const [hotelLogo, setHotelLogo] = useState('');
  const [hotelColor, setHotelColor] = useState('#3d489b');

  const cartItems = cart?.items ?? [];

  useEffect(() => {
    if (!sessionDeadline) return undefined;

    const updateCountdown = () => {
      const remaining = Math.ceil((sessionDeadline - Date.now()) / 1000);
      if (remaining <= 0) {
        setSessionSecondsRemaining(0);
        redirectToQrReader();
        return;
      }
      setSessionSecondsRemaining(remaining);
    };

    updateCountdown();
    const interval = window.setInterval(updateCountdown, 250);
    return () => window.clearInterval(interval);
  }, [sessionDeadline]);

  const refreshCart = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const apartamento = params.get('x');
      const cliente_url = params.get('id_cliente');
      const session_control = params.get('session_control');
      const ip = localStorage.getItem('ip_address');
      const session = localStorage.getItem('session');
      const language = navigator.language || navigator.userLanguage || 'pt-BR';

      const body = JSON.stringify({
        cliente_id: cliente_url,
        ip_address: ip,
        session,
        language,
      });

      const headers = { 'Content-Type': 'application/json' };

      const response = await fetch(
        `${process.env.REACT_APP_URL_API}/apiv2/cart/?x=${apartamento}&id_cliente=${cliente_url}&session_control=${session_control}&language=${language}`,
        { method: 'POST', headers, body },
      );

      const json = await response.json();
      const items = Array.isArray(json) ? json : (json.items || []);
      const normalized = { items };

      setCart(normalized);

      return normalized;
    } catch (err) {
      console.error('Erro ao buscar o carrinho:', err);
    }
  };

  // --- Função de carregamento dos dados globais ---
  useEffect(() => {
    const fetchGlobalData = async () => {
      try {
        setLoading(true);

        const params = new URLSearchParams(window.location.search);
        const apartamento = params.get('x');
        const cliente_url = params.get('id_cliente');
        const session_control = params.get('session_control');
        const tempoLimiteUrl =
          params.get('tempo_limite_sessao') || params.get('tempo_sessao');

        const ip = localStorage.getItem('ip_address');
        const session = localStorage.getItem('session');
        const language = navigator.language || navigator.userLanguage || 'pt-BR';

        const headers = { 'Content-Type': 'application/json' };

        const bodyIndex = JSON.stringify({
          cliente_id: cliente_url,
          ip_address: ip,
          x: apartamento,
          session,
          language,
        });

        const body = JSON.stringify({
          cliente_id: cliente_url,
          ip_address: ip,
          session,
          language,
        });

        const [resIndex, resMenu, resCart] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_URL_API}/apiv2/index/?x=${apartamento}&id_cliente=${cliente_url}&session_control=${session_control}&language=${language}`,
            { method: 'POST', headers, body: bodyIndex },
          ),
          fetch(
            `${process.env.REACT_APP_URL_API}/apiv2/menu/?x=${apartamento}&id_cliente=${cliente_url}&session_control=${session_control}&language=${language}`,
            { method: 'POST', headers, body },
          ),
          fetch(
            `${process.env.REACT_APP_URL_API}/apiv2/cart/?x=${apartamento}&id_cliente=${cliente_url}&session_control=${session_control}&language=${language}`,
            { method: 'POST', headers, body },
          ),
        ]);

        const jsonIndex = await resIndex.json();
        const jsonMenu = await resMenu.json();
        const jsonCart = await resCart.json();

        // A API pode retornar `cliente` como lista ou como objeto,
        // dependendo da versão do backend. Normalize para o formato usado
        // pelas telas do frontend.
        const cliente = Array.isArray(jsonIndex?.cliente)
          ? jsonIndex.cliente[0] || {}
          : jsonIndex?.cliente || {};
        // O tempo da sessão é uma duração em minutos. A contagem começa no
        // timestamp `session_control`, que representa o início da leitura do QR Code.
        const sessionLimit =
          tempoLimiteUrl ||
          jsonIndex?.tempo_limite_sessao ||
          cliente?.tempo_limite_sessao;

        if (isSessionExpired(session_control, sessionLimit)) {
          redirectToQrReader();
          return;
        }

        // Uma nova URL com session_control válido inicia uma nova sessão,
        // portanto remove uma eventual flag deixada pela sessão anterior.
        clearSessionExpiration();

        localStorage.setItem('tempo_limite_sessao', String(sessionLimit ?? ''));
        const deadline = getSessionDeadline(session_control, sessionLimit);
        if (deadline !== null) {
          setSessionDeadline(deadline);
        }
        const normalizedData = {
          ...jsonIndex,
          cliente,
          menu: jsonIndex?.menu || cliente.menu || [],
        };
        localStorage.setItem('cliente_logo', cliente.logo || '');
        setHotelLogo(cliente.logo || '');
        setHotelColor(
          cliente.cor_1 === 'outra'
            ? cliente.cor_custom || '#3d489b'
            : resolveColor(cliente.cor_1, '#3d489b'),
        );
        localStorage.setItem('cliente_cor1', cliente.cor_1 || '');
        localStorage.setItem('cliente_cor_custom', cliente.cor_custom || '');
        localStorage.setItem('cliente_cor_letra', cliente.cor_letra || '');
        localStorage.setItem('cliente_pedido_menu', String(cliente.pedido_menu ?? true));
        localStorage.setItem('cliente_mostra_ads_primeira_pagina', String(cliente.mostra_ads_primeira_pagina ?? false));
        localStorage.setItem('cliente_front_novo', String(cliente.front_novo ?? false));
        localStorage.setItem('cliente_apelido', cliente.apelido || '');

        const cartItemsData = Array.isArray(jsonCart) ? jsonCart : (jsonCart.items || []);
        const normalizedCart = { items: cartItemsData };

        setData(normalizedData);
        setMenu(jsonMenu);
        setCart(normalizedCart);
      } catch (err) {
        console.error('Erro ao carregar dados globais:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (!menu) {
      fetchGlobalData();
    }
  }, [menu]);

  const value = {
    cartItems,
    refreshCart,
    cart,
    data,
    menu,
    loading,
    error,
  };

  return (
    <GlobalContext.Provider value={value}>
      {children}
      <SessionExpirationModal
        seconds={sessionSecondsRemaining}
        logo={hotelLogo}
        color={hotelColor}
      />
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => useContext(GlobalContext);
